import { Menu } from '@systemeio/components'
import { ChevronDownIcon } from '@systemeio/icons'
import { LocaleEnum } from '@systemeio/types'
import { localeLabels, localeNames } from '@systemeio/util'
import React from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twMerge } from 'tailwind-merge'

export interface LanguageSwitcherProps {
  selectedLanguage: LocaleEnum
  changeLanguage: (locale: LocaleEnum) => void
  listOfLanguages?: LocaleEnum[]
  isFetching?: boolean
  /** Trigger label: `label` shows the short locale code, `name` shows the full language name. */
  labelType?: 'label' | 'name'
  buttonClassName?: string
  menuClassName?: string
  labelClassName?: string
  /** Whether hovering the trigger opens the desktop popup. Ignored on the mobile drawer surface. */
  openOnHover?: boolean
  dataTestId?: string
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  selectedLanguage,
  changeLanguage,
  listOfLanguages = Object.values(LocaleEnum),
  isFetching,
  labelType = 'label',
  buttonClassName,
  menuClassName,
  labelClassName,
  openOnHover,
  dataTestId,
}) => {
  const { t } = useLocoTranslation()

  return (
    <Menu
      disabled={isFetching}
      drawerTitle={t('global.language')}
      className={twMerge('tablet:h-79', menuClassName)}
      openOnHover={openOnHover}
      trigger={() => (
        <>
          <span className={twMerge('min-w-0 truncate font-medium', labelClassName)}>
            {labelType === 'label' ? localeLabels[selectedLanguage] : localeNames[selectedLanguage]}
          </span>
          <ChevronDownIcon className="transition-base size-5 shrink-0" />
        </>
      )}
      triggerProps={{
        className: twMerge(
          'text-icon-primary transition-base data-popup-open:bg-bg-tertiary relative flex min-w-0 items-center justify-between gap-0.5 rounded-md py-1.5 pr-1 pl-2 transition-colors data-popup-open:[&>svg]:rotate-180',
          'hover:bg-bg-tertiary',
          isFetching && 'animate-pulse',
          buttonClassName,
        ),
        'data-test-id': dataTestId,
      }}
      items={listOfLanguages.map(language => ({
        id: language,
        state: language === selectedLanguage ? 'active' : 'default',
        dataAttributes: {
          'data-test': `language-switcher-option-button-${language}`,
        },
        label: <span className="min-w-0 truncate">{localeNames[language]}</span>,
        onSelect: () => {
          if (language !== selectedLanguage) changeLanguage(language)
        },
      }))}
    />
  )
}
